import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CartProvider } from "./context/CartContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import { MessagesProvider } from "./context/MessagesContext";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import { UserType } from "./api/authService";

import ClientMainPage from "./Pages/Client/MainPage";
import ProductsList from "./Pages/Client/ProductsList";
import ProductDetails from "./Pages/Client/ProductDetails";
import StoresList from "./Pages/Client/StoresList";
import Wishlist from "./Pages/Client/Wishlist";
import Cart from "./Pages/Client/Cart";
import Checkout from "./Pages/Client/Checkout";
import ClientOrders from "./Pages/Client/Orders";
import ClientNotifications from "./Pages/Client/Notifications";
import ClientMessages from "./Pages/Client/Messages";
import Addresses from "./Pages/Client/Addresses";
import FollowedStores from "./Pages/Client/FollowedStores";
import MyCoupons from "./Pages/Client/MyCoupons";
import StoreNotifications from "./Pages/Client/StoreNotifications";
import LandingPage from "./Pages/LandingPage";

import AdminLogin from "./Pages/Admin/Login";
import AdminLayout from "./Pages/Admin/AdminLayout";
import Dashboard from "./Pages/Admin/Dashboard";
import Analytics from "./Pages/Admin/Analytics";
import SocialMedia from "./Pages/Admin/SocialMedia";
import StoreAnalyticsDetail from "./Pages/Admin/StoreAnalyticsDetail";
import CityAnalyticsDetail from "./Pages/Admin/CityAnalyticsDetail";
import DistrictAnalyticsDetail from "./Pages/Admin/DistrictAnalyticsDetail";
import Users from "./Pages/Admin/Users";
import AccountAccess from "./Pages/Admin/AccountAccess";
import ClientWarnings from "./Pages/Admin/ClientWarnings";
import WholesaleClients from "./Pages/Admin/WholesaleClients";
import Stores from "./Pages/Admin/Stores";
import PartnerRequests from "./Pages/Admin/PartnerRequests";
import Orders from "./Pages/Admin/Orders";
import OrderDetail from "./Pages/Admin/OrderDetail";
import DeleteOrders from "./Pages/Admin/DeleteOrders";
import Delivery from "./Pages/Admin/Delivery";
import DeliveryCompanies from "./Pages/Admin/DeliveryCompanies";
import Categories from "./Pages/Admin/Categories";
import ProductImports from "./Pages/Admin/ProductImports";
import Attributes from "./Pages/Admin/Attributes";
import AuthenticityOptions from "./Pages/Admin/AuthenticityOptions";
import DeliveryPricing from "./Pages/Admin/DeliveryPricing";
import Cities from "./Pages/Admin/Cities";
import Advertisements from "./Pages/Admin/Advertisements";
import Coupons from "./Pages/Admin/Coupons";
import GiftOffers from "./Pages/Admin/GiftOffers";
import FeesSubscriptions from "./Pages/Admin/FeesSubscriptions";
import AdminDeliveryCompanyStoreFees from "./Pages/Admin/DeliveryCompanyStoreFees";
import Settings from "./Pages/Admin/Settings";
import Chats from "./Pages/Admin/Chats";
import Notifications from "./Pages/Admin/Notifications";
import Reports from "./Pages/Admin/Reports";

import BusinessOwnerLogin from "./Pages/BusinessOwner/Login";
import BusinessOwnerLayout from "./Pages/BusinessOwner/BusinessOwnerLayout";
import BusinessOwnerDashboard from "./Pages/BusinessOwner/Dashboard";
import BusinessOwnerStores from "./Pages/BusinessOwner/Stores";
import BusinessOwnerStoreProfile from "./Pages/BusinessOwner/StoreProfile";
import BusinessOwnerStoreLayoutDesigner from "./Pages/BusinessOwner/StoreLayoutDesigner";
import BusinessOwnerStoreCarousel from "./Pages/BusinessOwner/StoreCarousel";
import BusinessOwnerProducts from "./Pages/BusinessOwner/Products";
import BusinessOwnerOrders from "./Pages/BusinessOwner/Orders";
import BusinessOwnerMessages from "./Pages/BusinessOwner/Messages";
import BusinessOwnerSettings from "./Pages/BusinessOwner/Settings";

import DeliveryLogin from "./Pages/Delivery/Login";
import DeliveryMainPage from "./Pages/Delivery/MainPage";

import DeliveryCompanyLogin from "./Pages/DelivryCompany/Login";
import DeliveryCompanyLayout from "./Pages/DelivryCompany/DeliveryCompanyLayout";
import DeliveryCompanyDashboard from "./Pages/DelivryCompany/Dashboard";
import DeliveryCompanyDrivers from "./Pages/DelivryCompany/Drivers";
import DeliveryCompanyOrders from "./Pages/DelivryCompany/Orders";
import DeliveryCompanyDriverAccess from "./Pages/DelivryCompany/DriverAccess";
import DeliveryCompanyFees from "./Pages/DelivryCompany/Fees";
import DeliveryCompanyStoreFees from "./Pages/DelivryCompany/StoreFees";

function App() {
  return (
    <LanguageProvider>
    <BrowserRouter>
    <AuthProvider>
      <WishlistProvider>
        <CartProvider>
          <NotificationsProvider>
            <MessagesProvider>
            <Routes>
              <Route path="/" element={<ClientMainPage />} />
              <Route path="/products" element={<ProductsList />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/stores" element={<StoresList />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/orders" element={<ClientOrders />} />
              <Route path="/notifications" element={<ClientNotifications />} />
              <Route path="/messages" element={<ClientMessages />} />
              <Route path="/addresses" element={<Addresses />} />
              <Route path="/followed-stores" element={<FollowedStores />} />
              <Route path="/my-coupons" element={<MyCoupons />} />
              <Route path="/store-notifications" element={<StoreNotifications />} />
              <Route path="/join" element={<LandingPage />} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredUserType={UserType.Admin} loginPath="/admin/login">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="analytics/stores/:id" element={<StoreAnalyticsDetail />} />
                <Route path="analytics/cities/:cityId" element={<CityAnalyticsDetail />} />
                <Route path="analytics/districts/:districtName" element={<DistrictAnalyticsDetail />} />
                <Route path="social-media" element={<SocialMedia />} />
                <Route path="users" element={<Users />} />
                <Route path="account-access" element={<AccountAccess />} />
                <Route path="client-warnings" element={<ClientWarnings />} />
                <Route path="wholesale-clients" element={<WholesaleClients />} />
                <Route path="stores" element={<Stores />} />
                <Route path="partner-requests" element={<PartnerRequests />} />
                <Route path="reports" element={<Reports />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/:id" element={<OrderDetail />} />
                <Route path="delete-orders" element={<DeleteOrders />} />
                <Route path="delivery" element={<Delivery />} />
                <Route path="delivery-companies" element={<DeliveryCompanies />} />
                <Route path="delivery-companies/store-fees" element={<AdminDeliveryCompanyStoreFees />} />
                <Route path="categories" element={<Categories />} />
                <Route path="product-imports" element={<ProductImports />} />
                <Route path="attributes" element={<Attributes />} />
                <Route path="authenticity-options" element={<AuthenticityOptions />} />
                <Route path="delivery-pricing" element={<DeliveryPricing />} />
                <Route path="cities" element={<Cities />} />
                <Route path="advertisements" element={<Advertisements />} />
                <Route path="coupons" element={<Coupons />} />
                <Route path="gift-offers" element={<GiftOffers />} />
                <Route path="fees-subscriptions" element={<FeesSubscriptions />} />
                <Route path="chats" element={<Chats />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              <Route path="/business-owner/login" element={<BusinessOwnerLogin />} />
              <Route
                path="/business-owner"
                element={
                  <ProtectedRoute requiredUserType={UserType.BusinessOwner} loginPath="/business-owner/login">
                    <BusinessOwnerLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<BusinessOwnerDashboard />} />
                <Route path="stores" element={<BusinessOwnerStores />} />
                <Route path="stores/:storeId" element={<BusinessOwnerStoreProfile />} />
                <Route path="stores/:storeId/layout" element={<BusinessOwnerStoreLayoutDesigner />} />
                <Route path="stores/:storeId/carousel" element={<BusinessOwnerStoreCarousel />} />
                <Route path="products" element={<BusinessOwnerProducts />} />
                <Route path="orders" element={<BusinessOwnerOrders />} />
                <Route path="messages" element={<BusinessOwnerMessages />} />
                <Route path="settings" element={<BusinessOwnerSettings />} />
              </Route>

              <Route path="/delivery/login" element={<DeliveryLogin />} />
              <Route
                path="/delivery"
                element={
                  <ProtectedRoute requiredUserType={UserType.Delivery} loginPath="/delivery/login">
                    <DeliveryMainPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/delivry-company/login" element={<DeliveryCompanyLogin />} />
              <Route
                path="/delivry-company"
                element={
                  <ProtectedRoute requiredUserType={UserType.DeliveryCompany} loginPath="/delivry-company/login">
                    <DeliveryCompanyLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DeliveryCompanyDashboard />} />
                <Route path="drivers" element={<DeliveryCompanyDrivers />} />
                <Route path="orders" element={<DeliveryCompanyOrders />} />
                <Route path="driver-access" element={<DeliveryCompanyDriverAccess />} />
                <Route path="fees" element={<DeliveryCompanyFees />} />
                <Route path="store-fees" element={<DeliveryCompanyStoreFees />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </MessagesProvider>
          </NotificationsProvider>
        </CartProvider>
      </WishlistProvider>
    </AuthProvider>
    </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
